import Swal from 'sweetalert2';

import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import LinkEditing from '@ckeditor/ckeditor5-link/src/linkediting';

import ArticleLinkIcon from '../icons/link-article.svg';

/*
  ArticleLinker options (editor.config.get('articleLinker')):
  - endpoint: URL base da API (opcional).
  - defaultParams: params fixos enviados junto com a busca (opcional).
  - queryParam: nome do param de busca (opcional).
  - pageParam: nome do param de pagina (opcional).
  - limitParam: nome do param de limite (opcional).
  - limit: tamanho da pagina (opcional).
  - labelPath: caminho do titulo no item retornado (opcional).
  - urlPath: caminho da URL no item retornado (opcional).
  - urlBase: base fixa para URLs relativas (opcional).
  - urlBasePath: caminho da base de URL no item retornado (opcional).
  - headers: headers extras para fetch (opcional).
  - apiKey: se definido, envia Authorization: ApiKey <valor> (sobrescreve Authorization em headers).
  - fetchOptions: opções extras passadas ao fetch (ex.: credentials, mode); merge de headers com os acima.

  Exemplo:
  ClassicEditor.create(el, {
    articleLinker: {
      endpoint: 'https://api.bs.vibra.digital/api/v1/BandArticle',
      apiKey: 'seu-token',
      headers: { Accept: 'application/json' },
      defaultParams: { sort: '-@created', qfields: 'config.order.data.title^1000' },
      queryParam: 'q2',
      pageParam: 'page',
      limitParam: 'limit',
      limit: 10,
      labelPath: 'config.order.data.title',
      urlPath: 'url',
      urlBasePath: 'route.map.template.config.audience.base_url'
    }
  });
*/

const DEFAULT_ENDPOINT = 'https://api.bs.vibra.digital/api/v1/BandArticle';

export function buildArticleLinkerFetchHeaders(options = {}) {
  const headers = {};

  if (options.headers && typeof options.headers === 'object') {
    Object.assign(headers, options.headers);
  }

  if (options.apiKey) {
    headers.Authorization = `ApiKey ${options.apiKey}`;
  }

  return headers;
}

function getPathValue(source, path) {
    if (!source || !path) {
        return undefined;
    }

    return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), source);
}

function buildArticleUrl(rawUrl, baseUrl) {
    if (!rawUrl) {
        return '';
    }

    if (/^https?:\/\//i.test(rawUrl)) {
        return rawUrl;
    }

    if (!baseUrl) {
        return rawUrl;
    }

    return `${baseUrl.replace(/\/$/, '')}/${rawUrl.replace(/^\//, '')}`;
}

function formatDate(value) {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '-';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} - ${hour}:${minute}`;
}

export default class ArticleLinker extends Plugin {
    static get pluginName() {
        return 'ArticleLinker';
    }

    static get requires() {
        return [LinkEditing];
    }

    init() {
        const editor = this.editor;
        const options = editor.config.get('articleLinker') || {};
        const endpoint = options.endpoint || DEFAULT_ENDPOINT;
        const defaultParams = options.defaultParams || {
            sort: '-@created',
            qfields: 'config.order.data.search_text',
        };
        const queryParam = options.queryParam || 'q2';
        const pageParam = options.pageParam || 'page';
        const limitParam = options.limitParam || 'limit';
        const limit = options.limit || 10;
        const labelPath = options.labelPath || 'config.order.data.title';
        const urlPath = options.urlPath || 'url';
        const urlBase = options.urlBase || null;
        const urlBasePath = options.urlBasePath || 'route.map.template.config.audience.base_url';

        const applyLink = (url) => {
            if (!url) {
                return;
            }

            if (editor.model.document.selection.isCollapsed) {
                Swal.fire({
                    icon: 'info',
                    text: 'Selecione um texto no editor para aplicar o link.',
                });
                return;
            }

            editor.execute('link', url);
            editor.editing.view.focus();
            Swal.close();
        };

        const buildUrl = (state) => {
            const url = new URL(endpoint, window.location.origin);
            const params = new URLSearchParams(url.search);

            Object.entries(defaultParams).forEach(([key, value]) => {
                if (!params.has(key) && value !== undefined && value !== null && value !== '') {
                    params.set(key, value);
                }
            });

            params.set(pageParam, String(state.page));
            params.set(limitParam, String(state.limit));

            if (state.query) {
                params.set(queryParam, state.query);
            } else {
                params.delete(queryParam);
            }

            url.search = params.toString();
            return url.toString();
        };

        const openDialog = () => {
            Swal.fire({
                title: 'Link para notícia',
                width: '60%',
                backdrop: true,
                showCancelButton: true,
                showConfirmButton: false,
                html: '<div id="ck-article-linker-root"></div>',
                onOpen: () => {
                    const container = Swal.getHtmlContainer();
                    if (!container) {
                        return;
                    }

                    const root = container.querySelector('#ck-article-linker-root');
                    if (!root) {
                        return;
                    }

                    root.innerHTML = `
                        <style>
                            .swal2-content {
                                padding: 0 !important;
                            }
                            .swal2-header h2 {
                                line-height: 1.5;
                            }
                            .ck-article-linker {
                                --ck-article-linker-results-height: 260px;
                                text-align: left;
                                font-family: "Inter", "Segoe UI", sans-serif;
                                color: #243b53;
                            }
                            .ck-article-linker__search {
                                display: flex;
                                gap: 12px;
                                align-items: center;
                                margin-bottom: 20px;
                            }
                            .ck-article-linker__search input {
                                flex: 1;
                                border-radius: 8px;
                                border: 1px solid #d9e2ec;
                                padding: 10px 12px;
                                font-size: 13px;
                                background: #f8fafc;
                            }
                            .ck-article-linker__status {
                                display: none;
                                font-size: 12px;
                                color: #52606d;
                                margin-bottom: 8px;
                            }
                            .ck-article-linker__results {
                                max-height: var(--ck-article-linker-results-height);
                                overflow: auto;
                                border: 1px solid #e6eaf0;
                                padding: 0;
                                border-radius: 10px;
                                background: #ffffff;
                                min-height: var(--ck-article-linker-results-height);
                            }
                            .ck-article-linker__header,
                            .ck-article-linker__row {
                                display: grid;
                                grid-template-columns: 40px minmax(220px, 1.5fr) minmax(120px, 1fr) 120px 110px 44px;
                                align-items: center;
                                gap: 12px;
                                padding: 10px 14px;
                            }
                            .ck-article-linker__header {
                                font-size: 13px;
                                font-weight: 600;
                                color: #b21f24;
                                background: #f5f7fa;
                                border-bottom: 1px solid #e6eaf0;
                                text-transform: uppercase;
                                letter-spacing: 0.04em;
                            }
                            .ck-article-linker__row {
                                border-bottom: 1px solid #eef1f5;
                                font-size: 13px;
                                cursor: pointer;
                                transition: background 0.15s ease;
                            }
                            .ck-article-linker__row:hover {
                                background: #f7f9fc;
                            }
                            .ck-article-linker__thumb {
                                width: 32px;
                                height: 32px;
                                border-radius: 50%;
                                background: #e6eaf0;
                                background-size: cover;
                                background-position: center;
                            }
                            .ck-article-linker__title {
                                font-weight: 400;
                                color: #243b53;
                                line-height: 1.2;
                            }
                            .ck-article-linker__meta {
                                color: #52606d;
                                line-height: 1.2;
                            }
                            .ck-article-linker__status-pill {
                                display: inline-flex;
                                align-items: center;
                                justify-content: center;
                                padding: 2px 6px;
                                border-radius: 999px;
                                font-size: 9px;
                                font-weight: 400;
                                letter-spacing: 0.04em;
                                text-transform: uppercase;
                                border: 1px solid transparent;
                            }
                            .ck-article-linker__status-pill--published {
                                color: #22c55e;
                                border-color: #22c55e;
                            }
                            .ck-article-linker__actions {
                                display: flex;
                                justify-content: flex-end;
                                margin-top: 12px;
                            }
                            .ck-article-linker__link {
                                position: relative;
                                display: inline-flex;
                                align-items: center;
                                justify-content: center;
                                width: 28px;
                                height: 28px;
                                border-radius: 8px;
                                border: 1px solid #e6eaf0;
                                color: #b21f24;
                                text-decoration: none;
                                transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
                            }
                            .ck-article-linker__link:hover {
                                background: #b21f24;
                                color: #ffffff;
                                border-color: #b21f24;
                            }
                            .ck-article-linker__link::after {
                                content: attr(data-tooltip);
                                position: absolute;
                                bottom: calc(100% + 6px);
                                left: 50%;
                                transform: translateX(-50%);
                                padding: 4px 8px;
                                border-radius: 6px;
                                background: rgba(15, 23, 42, 0.9);
                                color: #ffffff;
                                font-size: 11px;
                                white-space: nowrap;
                                opacity: 0;
                                pointer-events: none;
                                transition: opacity 0.15s ease;
                            }
                            .ck-article-linker__link:hover::after {
                                opacity: 1;
                            }
                            .ck-article-linker__link svg {
                                width: 14px;
                                height: 14px;
                            }
                            .ck-article-linker__btn {
                                border-radius: 8px;
                                padding: 6px 12px;
                                border: 1px solid #b21f24;
                                background: #ffffff;
                                color: #b21f24;
                                font-size: 12px;
                                font-weight: 600;
                                cursor: pointer;
                                margin-left: 10px;
                            }
                            .ck-article-linker__btn:hover {
                                border-radius: 8px;
                                padding: 6px 12px;
                                border: 1px solid #b21f24;
                                background: #b21f24;
                                color: #ffffff;
                                font-size: 12px;
                                font-weight: 600;
                                cursor: pointer;
                            }
                            .ck-article-linker__btn--primary {
                                background: #b21f24;
                                color: #ffffff;
                                border-color: #b21f24;
                                box-shadow: 0 2px 6px rgba(178, 31, 36, 0.25);
                            }
                            .ck-article-linker__btn[disabled] {
                                opacity: 0.65;
                                cursor: not-allowed;
                                background: #ffffff;
                                color: #b21f24;
                                border-color: #b21f24;
                                box-shadow: none;
                                pointer-events: none;
                            }
                            .ck-article-linker__btn.is-disabled {
                                opacity: 0.65;
                                cursor: not-allowed;
                                background: #f3f4f6;
                                color: #9aa5b1;
                                border-color: #e4e7eb;
                                box-shadow: none;
                                pointer-events: none;
                            }
                            .ck-article-linker__loading {
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                min-height: var(--ck-article-linker-results-height);
                                color: #52606d;
                                font-size: 13px;
                                gap: 8px;
                            }
                            .ck-article-linker__spinner {
                                width: 18px;
                                height: 18px;
                                border-radius: 50%;
                                border: 2px solid #d3d9e2;
                                border-top-color: #1d4ed8;
                                animation: ck-article-linker-spin 0.8s linear infinite;
                            }
                            @keyframes ck-article-linker-spin {
                                to { transform: rotate(360deg); }
                            }
                        </style>
                        <div class="ck-article-linker">
                            <div class="ck-article-linker__search">
                                <input id="ck-article-linker-query" placeholder="Buscar" />
                                <button id="ck-article-linker-search" type="button" class="ck-article-linker__btn ck-article-linker__btn--primary">Buscar</button>
                            </div>
                            <div id="ck-article-linker-status" class="ck-article-linker__status"></div>
                            <div id="ck-article-linker-results" class="ck-article-linker__results"></div>
                            <div class="ck-article-linker__actions">
                                <button id="ck-article-linker-prev" type="button" class="ck-article-linker__btn" disabled>Anterior</button>
                                <button id="ck-article-linker-next" type="button" class="ck-article-linker__btn">Próxima</button>
                            </div>
                        </div>
                    `;

                    const state = {
                        page: 1,
                        limit,
                        query: '',
                        total: 0,
                    };

                    const queryInput = root.querySelector('#ck-article-linker-query');
                    const searchButton = root.querySelector('#ck-article-linker-search');
                    const prevButton = root.querySelector('#ck-article-linker-prev');
                    const nextButton = root.querySelector('#ck-article-linker-next');
                    const status = root.querySelector('#ck-article-linker-status');
                    const results = root.querySelector('#ck-article-linker-results');

                    if (!queryInput || !searchButton || !prevButton || !nextButton || !status || !results) {
                        root.textContent = 'Erro ao montar interface.';
                        return;
                    }

                    queryInput.focus();

                    const setLoading = (loading) => {
                        searchButton.disabled = loading;
                        prevButton.disabled = loading;
                        nextButton.disabled = loading;
                        if (loading) {
                            results.innerHTML = `
                                <div class="ck-article-linker__loading">
                                    <span class="ck-article-linker__spinner"></span>
                                    Carregando notícias...
                                </div>
                            `;
                        }
                    };

                    const renderResults = (items) => {
                        results.textContent = '';

                        if (!items.length) {
                            results.innerHTML = '<div class="ck-article-linker__loading">Nenhum resultado.</div>';
                            return;
                        }

                        const list = document.createElement('div');
                        list.innerHTML = `
                            <div class="ck-article-linker__header">
                                <div></div>
                                <div>Título da matéria</div>
                                <div>Editoria principal</div>
                                <div>Data</div>
                                <div>Status</div>
                                <div></div>
                            </div>
                        `;

                        items.forEach((item) => {
                            const label = getPathValue(item, labelPath) || getPathValue(item, 'title') || 'Sem titulo';
                            const rawUrl = getPathValue(item, urlPath);
                            const baseFromItem = getPathValue(item, urlBasePath);
                            const finalUrl = buildArticleUrl(rawUrl, urlBase || baseFromItem);
                            const editoria = getPathValue(item, 'route.map.name') || '-';
                            const createdAt = getPathValue(item, '@created') || '-';
                            const statusLabel = 'PUBLICADO';
                            const statusClass = 'ck-article-linker__status-pill--published';
                            const thumbUrl = getPathValue(item, 'config.order.data.image.urlCrop') || getPathValue(item, 'config.order.data.image.url') || '';

                            const row = document.createElement('div');
                            row.className = 'ck-article-linker__row';
                            row.innerHTML = `
                                <div class="ck-article-linker__thumb" style="${thumbUrl ? `background-image:url('${thumbUrl}')` : ''}"></div>
                                <div class="ck-article-linker__title">${label}</div>
                                <div class="ck-article-linker__meta">${editoria}</div>
                                <div class="ck-article-linker__meta">${formatDate(createdAt)}</div>
                                <div><span class="ck-article-linker__status-pill ${statusClass}">${statusLabel}</span></div>
                                <div>
                                    <a class="ck-article-linker__link" href="${finalUrl}" target="_blank" rel="noopener noreferrer" aria-label="Abrir notícia em nova aba" data-tooltip="Abrir notícia">
                                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                            <path fill="currentColor" d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3z"></path>
                                            <path fill="currentColor" d="M5 5h7v2H7v10h10v-5h2v7H5V5z"></path>
                                        </svg>
                                    </a>
                                </div>
                            `;
                            row.addEventListener('click', () => applyLink(finalUrl));
                            const linkEl = row.querySelector('.ck-article-linker__link');
                            if (linkEl) {
                                linkEl.addEventListener('click', (event) => event.stopPropagation());
                            }

                            list.appendChild(row);
                        });

                        results.appendChild(list);
                    };

                    const updateStatus = () => {
                        const totalPages = Math.max(1, Math.ceil(state.total / state.limit));
                        status.textContent = `Página ${state.page} de ${totalPages} - ${state.total} notícias`;
                        const prevDisabled = state.page <= 1;
                        const nextDisabled = state.page >= totalPages;

                        prevButton.disabled = prevDisabled;
                        nextButton.disabled = nextDisabled;
                        prevButton.classList.toggle('is-disabled', prevDisabled);
                        nextButton.classList.toggle('is-disabled', nextDisabled);
                    };

                    const fetchAndRender = async () => {
                        setLoading(true);

                        try {
                            const fetchOpts = options.fetchOptions || {};
                            const { headers: fetchHeadersExtra, ...fetchRest } = fetchOpts;
                            const headers = {
                                ...buildArticleLinkerFetchHeaders(options),
                                ...(fetchHeadersExtra && typeof fetchHeadersExtra === 'object' ? fetchHeadersExtra : {}),
                            };

                            const response = await fetch(buildUrl(state), {
                                method: options.method || 'GET',
                                ...fetchRest,
                                headers,
                            });
                            if (!response.ok) {
                                throw new Error('Request failed');
                            }

                            const data = await response.json();
                            const items = Array.isArray(data.items) ? data.items : [];
                            state.total = Number.isFinite(data.total) ? data.total : items.length;
                            renderResults(items);
                            updateStatus();
                        } catch (err) {
                            results.textContent = 'Erro ao carregar notícias.';
                        } finally {
                            setLoading(false);
                        }
                    };

                    searchButton.addEventListener('click', () => {
                        state.query = queryInput.value.trim();
                        state.page = 1;
                        fetchAndRender();
                    });

                    queryInput.addEventListener('keydown', (event) => {
                        if (event.key === 'Enter') {
                            state.query = queryInput.value.trim();
                            state.page = 1;
                            fetchAndRender();
                        }
                    });

                    prevButton.addEventListener('click', () => {
                        if (prevButton.disabled) {
                            return;
                        }

                        if (state.page > 1) {
                            state.page -= 1;
                            fetchAndRender();
                        }
                    });

                    nextButton.addEventListener('click', () => {
                        if (nextButton.disabled) {
                            return;
                        }

                        state.page += 1;
                        fetchAndRender();
                    });

                    updateStatus();
                    fetchAndRender();
                },
            });
        };

        editor.ui.componentFactory.add('articleLinker', (locale) => {
            const view = new ButtonView(locale);

            view.set({
                label: 'Link para notícia',
                icon: ArticleLinkIcon,
                tooltip: true,
            });

            view.on('execute', () => {
                openDialog();
            });

            return view;
        });
    }
}
